import { ComputedRef } from 'vue'

import { DailyEffortsPresenter } from './dailyEffortsPresenter'
import { HowCategoryRatingPresenter } from './howCategoryRatingPresenter'
import { LanguageStatisticsPresenter } from './languageStatisticsPresenter'
import { ProgrammingCategoryRatingPresenter } from './programmingCategoryRatingPresenter'
import { SolvedProblemsPresenter } from './solvedProblemsPresenter'
import { SubmissionStatusPresenter } from './submissionStatusPresenter'
import { WeeklyRatingPresenter } from './weeklyRatingPresenter'
import { WhatCategoryRatingPresenter } from './whatCategoryRatingPresenter'

import { User } from '@/domain/entities/user/user'
import {
  dailyEffortsChartColor,
  dashboardTargetColor,
  howCategoryRatingChartColor,
  languageStatisticsChartColor,
  programmingCategoryRatingChartColor,
  solvedProblemsChartColor,
  submissionStatusChartColor,
  weeklyRatingChartColor,
  whatCategoryRatingChartColor
} from '@/domain/value_objects/color/ColorList'
import { DashboardTabType } from '@/domain/value_objects/dashboard/DashboardTabType'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { CategoryRatingViewModel } from '@/presentation/viewmodels/dashboard/categoryRatingViewModel'
import { DashboardModalStateViewModel } from '@/presentation/viewmodels/dashboard/dashboardModalStateViewModel'
import { StatisticsViewModel } from '@/presentation/viewmodels/dashboard/statisticsViewModel'
import { WeeklyRatingViewModel } from '@/presentation/viewmodels/dashboard/weeklyRatingViewModel'

export class DashboardModalStatePresenter {
  private viewModel: DashboardModalStateViewModel
  private weeklyRatingViewModel: WeeklyRatingViewModel
  private statisticsViewModel: StatisticsViewModel
  private categoryRatingViewModel: CategoryRatingViewModel
  private dailyEffortsPresenter: DailyEffortsPresenter
  private weeklyRatingPresenter: WeeklyRatingPresenter
  private solvedProblemsPresenter: SolvedProblemsPresenter
  private languageStatisticsPresenter: LanguageStatisticsPresenter
  private submissionStatusPresenter: SubmissionStatusPresenter
  private whatCategoryRatingPresenter: WhatCategoryRatingPresenter
  private howCategoryRatingPresenter: HowCategoryRatingPresenter
  private programmingCategoryRatingPresenter: ProgrammingCategoryRatingPresenter

  constructor(
    viewModel: DashboardModalStateViewModel,
    weeklyRatingViewModel: WeeklyRatingViewModel = new WeeklyRatingViewModel(),
    statisticsViewModel: StatisticsViewModel = new StatisticsViewModel(),
    categoryRatingViewModel: CategoryRatingViewModel = new CategoryRatingViewModel(),
    dailyEffortsPresenter: DailyEffortsPresenter = new DailyEffortsPresenter(statisticsViewModel),
    weeklyRatingPresenter: WeeklyRatingPresenter = new WeeklyRatingPresenter(weeklyRatingViewModel),
    solvedProblemsPresenter: SolvedProblemsPresenter = new SolvedProblemsPresenter(statisticsViewModel),
    languageStatisticsPresenter: LanguageStatisticsPresenter = new LanguageStatisticsPresenter(statisticsViewModel),
    submissionStatusPresenter: SubmissionStatusPresenter = new SubmissionStatusPresenter(),
    whatCategoryRatingPresenter: WhatCategoryRatingPresenter = new WhatCategoryRatingPresenter(categoryRatingViewModel),
    howCategoryRatingPresenter: HowCategoryRatingPresenter = new HowCategoryRatingPresenter(categoryRatingViewModel),
    programmingCategoryRatingPresenter: ProgrammingCategoryRatingPresenter = new ProgrammingCategoryRatingPresenter(
      categoryRatingViewModel
    )
  ) {
    this.viewModel = viewModel
    this.weeklyRatingViewModel = weeklyRatingViewModel
    this.statisticsViewModel = statisticsViewModel
    this.categoryRatingViewModel = categoryRatingViewModel
    this.dailyEffortsPresenter = dailyEffortsPresenter
    this.weeklyRatingPresenter = weeklyRatingPresenter
    this.solvedProblemsPresenter = solvedProblemsPresenter
    this.languageStatisticsPresenter = languageStatisticsPresenter
    this.submissionStatusPresenter = submissionStatusPresenter
    this.whatCategoryRatingPresenter = whatCategoryRatingPresenter
    this.howCategoryRatingPresenter = howCategoryRatingPresenter
    this.programmingCategoryRatingPresenter = programmingCategoryRatingPresenter
  }

  get dashboardUserList(): ComputedRef<User[]> {
    return this.viewModel.dashboardUserList
  }

  get dashboardProgramLanguage(): ComputedRef<ProgramLanguageType | 'All'> {
    return this.viewModel.dashboardProgramLanguage
  }

  get initialDashboardUser(): ComputedRef<User | null> {
    return this.viewModel.initialDashboardUser
  }

  get courseSerial(): ComputedRef<number> {
    return this.viewModel.courseSerial
  }

  get tabState(): ComputedRef<DashboardTabType> {
    return this.viewModel.tabState
  }

  get isAddingDashboardUser(): ComputedRef<boolean> {
    return this.viewModel.isAddingDashboardUser
  }

  get isLoading(): ComputedRef<boolean> {
    return this.viewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.viewModel.error
  }

  async addDashboardUser(userId: string, displayLanguage: DisplayLanguageType, isLightMode: boolean): Promise<void> {
    this.viewModel.setIsAddingDashboardUser(true)
    const user = await this.viewModel.isExistUser(userId)
    if (!user) {
      this.viewModel.setIsAddingDashboardUser(false)
      return
    }

    await this.viewModel.addDashboardUser(user)
    await this.weeklyRatingViewModel.addUserWeeklyRating(userId)
    await this.statisticsViewModel.addUserStatistics(userId)
    await this.categoryRatingViewModel.addUserCategoryRating(userId)

    this.reloadCharts(displayLanguage, isLightMode)
    this.viewModel.setIsAddingDashboardUser(false)
  }

  reloadCharts(displayLanguage: DisplayLanguageType, isLightMode: boolean): void {
    this.dailyEffortsPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      dailyEffortsChartColor,
      dashboardTargetColor,
      displayLanguage,
      isLightMode
    )
    this.solvedProblemsPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      solvedProblemsChartColor,
      dashboardTargetColor,
      displayLanguage,
      isLightMode
    )
    this.weeklyRatingPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      weeklyRatingChartColor,
      dashboardTargetColor,
      displayLanguage,
      isLightMode
    )
    this.languageStatisticsPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      languageStatisticsChartColor,
      dashboardTargetColor,
      displayLanguage,
      isLightMode
    )
    this.submissionStatusPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      submissionStatusChartColor,
      dashboardTargetColor,
      displayLanguage,
      isLightMode
    )
    this.reloadRadarCharts(displayLanguage, isLightMode)
  }

  reloadRadarCharts(displayLanguage: DisplayLanguageType, isLightMode: boolean) {
    this.whatCategoryRatingPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      whatCategoryRatingChartColor,
      dashboardTargetColor,
      this.viewModel.dashboardProgramLanguage.value,
      displayLanguage,
      isLightMode
    )
    this.howCategoryRatingPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      howCategoryRatingChartColor,
      dashboardTargetColor,
      this.viewModel.dashboardProgramLanguage.value,
      displayLanguage,
      isLightMode
    )
    this.programmingCategoryRatingPresenter.initChart(
      this.viewModel.dashboardUserList.value,
      programmingCategoryRatingChartColor,
      dashboardTargetColor,
      this.viewModel.dashboardProgramLanguage.value,
      displayLanguage,
      isLightMode
    )
  }

  clearTargetUsers = (displayLanguage: DisplayLanguageType, isLightMode: boolean) => {
    if (this.viewModel.dashboardUserList.value.length > 1) {
      this.viewModel.removeTargetUsers()
      this.weeklyRatingViewModel.removeTargetUsers()
      this.statisticsViewModel.removeTargetUsers()
      this.categoryRatingViewModel.removeTargetUsers()

      this.reloadCharts(displayLanguage, isLightMode)
    }
  }

  async isExistUser(userId: string): Promise<User> {
    return await this.viewModel.isExistUser(userId)
  }

  removeDashboardUserList(): void {
    this.viewModel.removeDashboardUserList()
  }

  removeTargetUsers(): void {
    this.viewModel.removeTargetUsers()
  }

  setInitialDashboardUser(user: User | null): void {
    this.viewModel.setInitialDashboardUser(user)
  }

  setDashboardProgramLanguage(language: ProgramLanguageType | 'All'): void {
    this.viewModel.setDashboardProgramLanguage(language)
  }

  setCourseSerial(serial: number): void {
    this.viewModel.setCourseSerial(serial)
  }

  setTabState(tabState: DashboardTabType): void {
    this.viewModel.setTabState(tabState)
  }
}
